import React from 'react';
import { AutoSizer, List, ListRowProps, Size } from 'react-virtualized';
import { padStart } from 'lodash';
import Prism from 'prismjs';

import { parseText } from '@neptune/shared/common-util';
import { Banner, bemBlock, Layout } from '@neptune/shared/venus-ui';

import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-docker';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-r';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-diff';
import 'prismjs/components/prism-batch';

import { CopyToClipboard } from '../copy-to-clipboard/CopyToClipboard';

import { TokenRenderer } from './TokenRenderer';

import './FastSyntaxHighlighter.less';

interface FastSyntaxHighlighterProps {
  className?: string;
  code: string;
  language?: string;
}

const LINE_TOKENIZATION_LIMIT = 1024;

const block = bemBlock('fast-syntax-highlighter');

export const FastSyntaxHighlighter = React.memo(function FastSyntaxHighlighter({
  className,
  code,
  language,
}: FastSyntaxHighlighterProps) {
  const lines = parseText(code);

  const padLength = String(lines.length).length;

  const grammar =
    language && Prism.languages[language] ? Prism.languages[language] : Prism.languages['clike']; // this should not happen

  const lineLengthExceeded = lines.some((line) => line.length > LINE_TOKENIZATION_LIMIT);

  function rowRenderer(props: ListRowProps) {
    const { key, index, style } = props;

    const line = lines[index];
    const tokens = line.length <= LINE_TOKENIZATION_LIMIT ? Prism.tokenize(line, grammar) : [line];

    let offset = 0;
    const children = tokens.map((token, index) => {
      const text = line.slice(offset, offset + token.length);
      offset += token.length;

      if (typeof token !== 'string') {
        return (
          <TokenRenderer
            key={index}
            tokenType={token.type}
            className={block('renderer')}
            data-role="syntax-token"
          >
            {text}
          </TokenRenderer>
        );
      }

      return (
        <span data-heap-redact-text key={index} data-role="syntax-token">
          {token}
        </span>
      );
    });

    return (
      <div style={style} className={block('row')} key={key} data-row={index}>
        <span
          className={block('line-number')}
          data-role="line-number"
        >{`${padStart(String(index + 1), padLength, ' ')}  `}</span>
        {children}
        {'\n'}
      </div>
    );
  }

  const content = (
    <AutoSizer>
      {({ height, width }: Size) => (
        <List
          rowRenderer={rowRenderer}
          rowHeight={20}
          rowCount={lines.length}
          height={height}
          width={width}
          overscanRowCount={200}
          className={block('list')}
          containerProps={{
            'data-role': 'syntax-preview-content',
          }}
        />
      )}
    </AutoSizer>
  );

  return (
    <Layout.Column className={block()}>
      {lineLengthExceeded && (
        <Banner.Warning size="small" data-role="syntax-preview-limit-exceeded-warning">
          Syntax highlighting was disabled on some lines exceeding the length limit (
          {LINE_TOKENIZATION_LIMIT} characters).
        </Banner.Warning>
      )}

      <div className={block({ element: 'content', extra: className })}>
        {content}

        <Layout.Row className={block('buttons')} alignItems="center">
          <CopyToClipboard text={code} />
        </Layout.Row>
      </div>
    </Layout.Column>
  );
});
